'use client';

import { Box, Container, useTheme } from '@mui/material';
import Image from 'next/image';
import { ReactNode, useMemo } from 'react';

import { ResourceCenterBreadcrumbs } from '@/components/ResourceCenter/ResourceCenterBreadcrumbs';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { ResourceCategory, UploadFile } from '@/types';

interface ResourceCenterHeroProps {
  children?: ReactNode;
  resourceCategory?: ResourceCategory;
}

export function ResourceCenterHero({ children, resourceCategory }: ResourceCenterHeroProps) {
  const theme = useTheme();
  const heroImage = useMemo(
    () => resourceCategory?.CategoryHero as UploadFile | undefined,
    [resourceCategory]
  );

  const heroImageUrl = useMemo(
    () => getImageUrl({ image: heroImage, size: 'large' }) || '/assets/hero_image.png',
    [heroImage]
  );

  return (
    <Box
      data-component="ResourceCenterHero"
      sx={{
        position: 'relative',
        pt: 4,
        pb: 6,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: '50%',
          gap: theme.spacing(10),
        }}
      >
        <Box
          sx={() => ({
            flexGrow: 1,
            paddingInline: 1,
          })}
        >
          {resourceCategory && <ResourceCenterBreadcrumbs resourceCategory={resourceCategory} />}
          <Box
            sx={{
              h2: {
                fontSize: `calc(${theme.typography.h2.fontSize} + 0.125rem )`,
              },
            }}
          >
            {children}
          </Box>
        </Box>
        <Box
          sx={{
            maxWidth: '50%',
            height: 'fit-content',
            backgroundColor: 'purple.500',
            padding: 1,
            borderRadius: 4,
          }}
        >
          <Image
            src={heroImageUrl}
            alt="Hero Image"
            width={heroImage?.width || 0}
            height={heroImage?.height || 0}
            priority
            fetchPriority="high"
            style={{
              objectFit: 'cover',
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '1.875rem',
              marginBottom: '-0.25rem',
            }}
          />
        </Box>
      </Container>
    </Box>
  );
}
